'use client'

/**
 * SSE transport for the assistant API. Generated RTK mutations do not parse
 * `text/event-stream` responses; this module handles streaming and abort.
 */

import { isObject, isString } from 'lodash'

import envConfig from '~/configs/env'
import { getAccessTokenSilently } from '~/data/api/client/security'
import parseBillingDraft from '~/features/billing-composer/parseBillingDraft'

import type {
  AssistantContextRef,
  AssistantPageContext,
  AssistantSendMessageParams,
  GenerativeDashboardRefreshParams
} from '~/data/api/client'
import { type AssistantAttachment, parseAssistantAttachment } from './attachments'
import type {
  AssistantClarificationQuestion,
  AssistantDashboardLayoutTile,
  AssistantStreamEvent
} from './streamTypes'

type SendMessageParams = {
  conversationId: string
  organizationId: string
  message: string
  contextRefs: AssistantContextRef[]
  pageContext: AssistantPageContext
  autoApprove: boolean
  skillId?: string
  /** Ids of attachments already uploaded to this conversation (contract §7, max 5). */
  attachmentIds?: AssistantSendMessageParams['attachmentIds']
  signal: AbortSignal
  onEvent: (event: AssistantStreamEvent) => void
}

export async function streamAssistantMessage(params: SendMessageParams): Promise<void> {
  const {
    conversationId,
    organizationId,
    message,
    contextRefs,
    pageContext,
    autoApprove,
    skillId,
    attachmentIds,
    signal,
    onEvent
  } = params
  const token = await getAccessTokenSilently()
  const body: AssistantSendMessageParams = {
    message,
    contextRefs,
    pageContext,
    autoApprove,
    ...(skillId ? { skillId } : {}),
    ...(attachmentIds && attachmentIds.length > 0 ? { attachmentIds } : {})
  }
  const response = await fetch(`${getAssistantBaseUrl()}/api/v1/assistant/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Organization-Id': organizationId
    },
    body: JSON.stringify(body),
    signal
  })

  if (!response.ok) {
    throw new Error(`Send message failed (${response.status})`)
  }
  await consumeEventStream(response, onEvent)
}

export type UploadAttachmentParams = {
  organizationId: string
  conversationId: string
  file: File
  signal?: AbortSignal
  /** 0–100, from the browser's upload progress events. */
  onProgress?: (percent: number) => void
}

/** Thrown by {@link uploadAssistantAttachment}; `code` maps the backend's 413/415 onto the chip error states. */
export class AssistantAttachmentUploadError extends Error {
  readonly code: 'tooLarge' | 'unsupportedType' | 'uploadFailed'
  readonly status: number | undefined

  constructor(code: AssistantAttachmentUploadError['code'], status?: number) {
    super(`Attachment upload failed${status ? ` (${status})` : ''}`)
    this.name = 'AssistantAttachmentUploadError'
    this.code = code
    this.status = status
  }
}

/**
 * Uploads one file to a conversation (multipart, field `file`) and returns the stored attachment's metadata.
 * Hand-rolled on `XMLHttpRequest` because RTK codegen does not support multipart upload progress; the response
 * is typed as the generated {@link AssistantAttachment} (narrowed after parse). The attachment id is then passed
 * to the next `streamAssistantMessage` via `attachmentIds`.
 */
export async function uploadAssistantAttachment(params: UploadAttachmentParams): Promise<AssistantAttachment> {
  const { organizationId, conversationId, file, signal, onProgress } = params
  const token = await getAccessTokenSilently()
  if (signal?.aborted) throw abortError()

  const url = `${getAssistantBaseUrl()}/api/v1/assistant/conversations/${conversationId}/attachments`
  const body = new FormData()
  body.append('file', file, file.name)

  return new Promise<AssistantAttachment>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', url)
    request.setRequestHeader('Authorization', `Bearer ${token}`)
    request.setRequestHeader('X-Organization-Id', organizationId)
    request.setRequestHeader('Accept', 'application/json')
    request.responseType = 'text'

    const onAbort = () => request.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const cleanup = () => signal?.removeEventListener('abort', onAbort)

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(Math.min(99, Math.round((event.loaded / event.total) * 100)))
      }
    }
    request.onerror = () => {
      cleanup()
      reject(new AssistantAttachmentUploadError('uploadFailed'))
    }
    request.onabort = () => {
      cleanup()
      reject(abortError())
    }
    request.onload = () => {
      cleanup()
      if (request.status === 413) return reject(new AssistantAttachmentUploadError('tooLarge', 413))
      if (request.status === 415) return reject(new AssistantAttachmentUploadError('unsupportedType', 415))
      if (request.status < 200 || request.status >= 300) {
        return reject(new AssistantAttachmentUploadError('uploadFailed', request.status))
      }
      const attachment = parseAttachmentResponse(request.responseText)
      if (!attachment) return reject(new AssistantAttachmentUploadError('uploadFailed', request.status))
      onProgress?.(100)
      resolve(attachment)
    }
    request.send(body)
  })
}

function parseAttachmentResponse(text: string): AssistantAttachment | null {
  try {
    return parseAssistantAttachment(JSON.parse(text))
  } catch {
    return null
  }
}

function abortError(): Error {
  const error = new Error('Attachment upload aborted')
  error.name = 'AbortError'
  return error
}

type RefreshDashboardParams = {
  organizationId: string
  tiles: GenerativeDashboardRefreshParams['tiles']
  signal: AbortSignal
  onEvent: (event: AssistantStreamEvent) => void
  /** The service serving the refresh; syn-ops through the platform gateway when absent. */
  baseUrl?: string
}

/**
 * Re-resolves a saved dashboard's tiles without the model: the backend runs each tile's stored read binding in
 * parallel and streams refreshed `layout` + `panel` events. `tiles` is reconstructed from the saved panels' provenance.
 *
 * This is a hand-rolled SSE call (not the generated `useRefreshMutation`) for the same reason as the other assistant
 * streams: the endpoint returns `text/event-stream`, which RTK Query / the generated client cannot consume. The request
 * body is typed with the generated `GenerativeDashboardRefreshParams` to stay aligned with the API contract.
 */
export async function streamDashboardRefresh(params: RefreshDashboardParams): Promise<void> {
  const { organizationId, tiles, signal, onEvent, baseUrl = getAssistantBaseUrl() } = params
  const token = await getAccessTokenSilently()
  const body: GenerativeDashboardRefreshParams = { tiles }
  const response = await fetch(`${baseUrl}/api/v1/generative-dashboard/refresh`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Organization-Id': organizationId
    },
    body: JSON.stringify(body),
    signal
  })

  if (!response.ok) {
    throw new Error(`Dashboard refresh failed (${response.status})`)
  }
  await consumeEventStream(response, onEvent)
}

type RunActionParams = {
  organizationId: string
  actionId: string
  signal: AbortSignal
  onEvent: (event: AssistantStreamEvent) => void
}

// Runs an approved action and streams the assistant's follow-up turn (it may
// confirm success, or — on failure — diagnose the API error and re-propose a fix).
export async function runAssistantAction(params: RunActionParams): Promise<void> {
  const { organizationId, actionId, signal, onEvent } = params
  const token = await getAccessTokenSilently()
  const response = await fetch(`${getAssistantBaseUrl()}/api/v1/assistant/actions/${actionId}/run`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Organization-Id': organizationId
    },
    signal
  })

  if (!response.ok) {
    throw new Error(`Run action failed (${response.status})`)
  }
  await consumeEventStream(response, onEvent)
}

type RejectActionParams = {
  organizationId: string
  actionId: string
  projectId?: string
  taskId?: string
}

/** Reject a still-proposed action so it cannot be approved/run. */
export async function rejectAssistantAction(params: RejectActionParams): Promise<void> {
  const { organizationId, actionId, projectId, taskId } = params
  const token = await getAccessTokenSilently()
  const qs = new URLSearchParams()
  if (projectId) qs.set('projectId', projectId)
  if (taskId) qs.set('taskId', taskId)
  const query = qs.toString()
  const response = await fetch(
    `${getAssistantBaseUrl()}/api/v1/assistant/actions/${actionId}/reject${query ? `?${query}` : ''}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Organization-Id': organizationId
      }
    }
  )
  if (!response.ok) {
    throw new Error(`Reject action failed (${response.status})`)
  }
}

type StreamActionProgressParams = {
  organizationId: string
  actionId: string
  signal: AbortSignal
  onEvent: (event: AssistantStreamEvent) => void
}

/** Subscribes to live progress for a long-running action (diagnostics phases, analysis tools). */
export async function streamAssistantActionProgress(params: StreamActionProgressParams): Promise<void> {
  const { organizationId, actionId, signal, onEvent } = params
  const token = await getAccessTokenSilently()
  const response = await fetch(`${getAssistantBaseUrl()}/api/v1/assistant/actions/${actionId}/stream`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
      'X-Organization-Id': organizationId
    },
    signal
  })

  if (!response.ok) {
    throw new Error(`Action progress stream failed (${response.status})`)
  }
  await consumeEventStream(response, onEvent)
}

async function consumeEventStream(
  response: Response,
  onEvent: (event: AssistantStreamEvent) => void
): Promise<void> {
  if (!response.body) {
    throw new Error('Assistant stream response body is empty')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let receivedDone = false

  while (true) {
    const { done, value } = await reader.read()
    if (value) {
      buffer += decoder.decode(value, { stream: true })
    }

    const blocks = buffer.split('\n\n')
    buffer = blocks.pop() ?? ''

    for (const block of blocks) {
      if (emitBlock(block, onEvent)) {
        receivedDone = true
      }
    }

    if (done) {
      if (buffer.trim()) {
        if (emitBlock(buffer, onEvent)) {
          receivedDone = true
        }
      }
      if (!receivedDone) {
        onEvent({ type: 'done' })
      }
      return
    }
  }
}

/**
 * Parses a single SSE block. Spring's SseEmitter writes `data:{...}` (no space
 * after the colon), and a block may contain multiple `data:` lines that the SSE
 * spec joins with newlines — handle both.
 * @returns true if a `done` event was emitted
 */
function emitBlock(block: string, onEvent: (event: AssistantStreamEvent) => void): boolean {
  const dataPayload = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''))
    .join('\n')
  if (!dataPayload) return false

  const parsedEvent = parseStreamEvent(dataPayload)
  if (parsedEvent) {
    onEvent(parsedEvent)
    return parsedEvent.type === 'done' || parsedEvent.type === 'cancelled'
  }
  return false
}

/** The backend sends `preview` as either text or the structured request body; the UI shows both as text. */
function serialisePreview(preview: unknown): string | undefined {
  if (isString(preview)) return preview.trim() || undefined
  if (preview === null || preview === undefined) return undefined
  try {
    return JSON.stringify(preview, null, 2)
  } catch {
    return undefined
  }
}

/** Parses one SSE `data:` payload into a typed event; unknown or malformed event types yield null and are dropped. */
export function parseStreamEvent(payloadText: string): AssistantStreamEvent | null {
  try {
    const parsed = JSON.parse(payloadText) as Record<string, unknown>
    const type = parsed.type
    if (!isString(type)) return null

    if (type === 'delta') {
      return { type: 'delta', text: isString(parsed.text) ? parsed.text : '' }
    }
    if (type === 'thinking_delta') {
      return {
        type: 'thinking_delta',
        text: isString(parsed.text) ? parsed.text : isString(parsed.thinking) ? parsed.thinking : ''
      }
    }
    if (type === 'tool_call') {
      return {
        type: 'tool_call',
        name: isString(parsed.name) ? parsed.name : 'tool',
        input: isObject(parsed.input) ? (parsed.input as Record<string, unknown>) : undefined
      }
    }
    if (type === 'tool_result') {
      return {
        type: 'tool_result',
        name: isString(parsed.name) ? parsed.name : 'tool',
        summary: isString(parsed.summary) ? parsed.summary : undefined
      }
    }
    if (type === 'propose_action') {
      return {
        type: 'propose_action',
        actionId: isString(parsed.actionId) ? parsed.actionId : '',
        reason: isString(parsed.reason) ? parsed.reason : undefined,
        actionType: isString(parsed.actionType) ? parsed.actionType : undefined,
        longRunning: parsed.longRunning === true,
        preview: serialisePreview(parsed.preview)
      }
    }
    if (type === 'action_stream') {
      return {
        type: 'action_stream',
        actionId: isString(parsed.actionId) ? parsed.actionId : '',
        actionType: isString(parsed.actionType) ? parsed.actionType : undefined,
        longRunning: parsed.longRunning === true
      }
    }
    if (type === 'phase') {
      const substatus =
        isString(parsed.substatus) ? parsed.substatus
        : isString(parsed.diagnosticsStatus) ? parsed.diagnosticsStatus
        : undefined
      return {
        type: 'phase',
        phase: isString(parsed.phase) ? parsed.phase : '',
        message: isString(parsed.message) ? parsed.message : '',
        substatus
      }
    }
    if (type === 'artifact') {
      return {
        type: 'artifact',
        filename: isString(parsed.filename) ? parsed.filename : 'artifact',
        contentType: isString(parsed.contentType) ? parsed.contentType : 'application/octet-stream',
        url: isString(parsed.url) ? parsed.url : undefined,
        sizeBytes: typeof parsed.sizeBytes === 'number' ? parsed.sizeBytes : undefined,
        inline: parsed.inline === true
      }
    }
    if (type === 'action_complete') {
      return {
        type: 'action_complete',
        status: isString(parsed.status) ? parsed.status : 'unknown',
        messageId: isString(parsed.messageId) ? parsed.messageId : undefined,
        summary: isString(parsed.summary) ? parsed.summary : undefined,
        error: isString(parsed.error) ? parsed.error : undefined
      }
    }
    if (type === 'layout') {
      return {
        type: 'layout',
        tiles: Array.isArray(parsed.tiles) ? parseLayoutTiles(parsed.tiles) : []
      }
    }
    if (type === 'panel') {
      return {
        type: 'panel',
        panel: {
          panelId: isString(parsed.panelId) ? parsed.panelId : `panel-${Date.now()}`,
          panelType: isString(parsed.panelType) ? parsed.panelType : 'narrative',
          title: isString(parsed.title) ? parsed.title : undefined,
          panelConfig: isObject(parsed.panelConfig) ? (parsed.panelConfig as Record<string, unknown>) : {},
          summaryText: isString(parsed.summaryText) ? parsed.summaryText : undefined,
          provenance: parsed.provenance,
          tileId: isString(parsed.tileId) ? parsed.tileId : undefined,
          status: 'ready'
        }
      }
    }
    if (type === 'clarification') {
      return {
        type: 'clarification',
        intro: isString(parsed.intro) ? parsed.intro : undefined,
        questions: Array.isArray(parsed.questions)
          ? (parsed.questions as AssistantClarificationQuestion[])
          : []
      }
    }
    if (type === 'billing_draft') {
      const draft = parseBillingDraft(parsed, `bdraft-${Date.now()}`)
      return draft ? { type: 'billing_draft', draft } : null
    }
    if (type === 'done') {
      return { type: 'done' }
    }
    if (type === 'cancelled') {
      return { type: 'cancelled' }
    }
    if (type === 'error') {
      return { type: 'error', message: isString(parsed.message) ? parsed.message : 'Unknown error' }
    }
  } catch {
    return null
  }
  return null
}

/** Coerces the raw `tiles` array of a `layout` event into well-formed skeleton tiles, dropping invalid entries. */
function parseLayoutTiles(raw: unknown[]): AssistantDashboardLayoutTile[] {
  return raw
    .filter(isObject)
    .map((tile) => tile as Record<string, unknown>)
    .filter((tile) => isString(tile.tileId))
    .map((tile) => ({
      tileId: tile.tileId as string,
      panelType: isString(tile.panelType) ? tile.panelType : 'narrative',
      title: isString(tile.title) ? tile.title : undefined
    }))
}

function getAssistantBaseUrl(): string {
  return envConfig.apiUrl
}
